import { Box, Divider, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { footerLogo } from '../../assets/index'
import locationIcon from '../../assets/icons/footer/location.png'
import phoneIcon from '../../assets/icons/footer/phone.png'
import mailIcon from '../../assets/icons/footer/mail.png'
import globeIcon from '../../assets/icons/footer/globe.png'
import socialYoutubeIcon from '../../assets/icons/footer/social-youtube.png'
import socialXIcon from '../../assets/icons/footer/social-x.png'
import socialInstagramIcon from '../../assets/icons/footer/social-instagram.png'
import { BUTTON_TRANSITION } from '../../theme/buttonHover'
import socialFacebookIcon from '../../assets/icons/footer/social-facebook.png'
import {
  contactInfo,
  footerGetInvolvedLinks,
  footerProgramLinks,
  footerQuickLinks,
  socialLinks,
} from '../../data/navigation'
import {
  FOOTER_MOBILE_MQ,
  footerBrandGap,
  footerColumnGap,
  footerPaddingX,
  footerPaddingY,
  footerSectionGap,
} from './footerLayout'

const SOCIAL_ICON_MAP = {
  YouTube: socialYoutubeIcon,
  X: socialXIcon,
  Instagram: socialInstagramIcon,
  Facebook: socialFacebookIcon,
}

const BRAND_DESCRIPTION =
  'Empowering youth through leadership development, cultural initiatives, and nation-building activities since 2011.'

function FooterColumn({ title, children, sx = {} }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '16px', lg: '20px' },
        minWidth: 0,
        width: '100%',
        [FOOTER_MOBILE_MQ]: { gap: '16px' },
        ...sx,
      }}
    >
      <Typography
        sx={{
          fontSize: { xs: 22, lg: 24 },
          lineHeight: 'normal',
          fontWeight: 400,
          color: '#111',
          [FOOTER_MOBILE_MQ]: { fontSize: 22 },
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  )
}

function FooterLink({ to, children }) {
  return (
    <Box
      component={RouterLink}
      to={to}
      sx={{
        display: 'block',
        color: '#111',
        textDecoration: 'none',
        fontSize: { xs: 15, lg: 16 },
        lineHeight: { xs: '15px', lg: '20px' },
        wordBreak: 'break-word',
        [FOOTER_MOBILE_MQ]: { fontSize: 15, lineHeight: '15px' },
        '&:hover': { opacity: 0.7 },
      }}
    >
      {children}
    </Box>
  )
}

function ContactRow({ icon, children, href }) {
  const content = (
    <Box sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start', minWidth: 0 }}>
      <Box
        component="img"
        src={icon}
        alt=""
        sx={{
          width: 20,
          height: 20,
          display: 'block',
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: { xs: 15, lg: 16 },
          lineHeight: 'normal',
          color: '#111',
          wordBreak: 'break-word',
          minWidth: 0,
          pt: '2.5px',
          [FOOTER_MOBILE_MQ]: { fontSize: 15 },
        }}
      >
        {children}
      </Typography>
    </Box>
  )

  if (href) {
    return (
      <Box
        component="a"
        href={href}
        sx={{ color: 'inherit', textDecoration: 'none', minWidth: 0, '&:hover': { opacity: 0.7 } }}
      >
        {content}
      </Box>
    )
  }

  return content
}

function SocialButton({ label, url }) {
  return (
    <Box
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      sx={{
        width: 45,
        height: 45,
        display: 'block',
        flexShrink: 0,
        textDecoration: 'none',
        transition: BUTTON_TRANSITION,
        '&:hover': { opacity: 0.85 },
      }}
    >
      <Box
        component="img"
        src={SOCIAL_ICON_MAP[label]}
        alt={label}
        sx={{
          width: '100%',
          height: '100%',
          display: 'block',
        }}
      />
    </Box>
  )
}

function LinkList({ links }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '9px', lg: '10px' },
        [FOOTER_MOBILE_MQ]: { gap: '9px' },
      }}
    >
      {links.map((link) => (
        <FooterLink key={link.path + link.label} to={link.path}>
          {link.label}
        </FooterLink>
      ))}
    </Box>
  )
}

export default function Footer() {
  return (
    <Box
      component="footer"
      sx={{
        width: '100%',
        minWidth: 320,
        bgcolor: 'rgba(8, 39, 83, 0.06)',
        py: footerPaddingY,
        px: footerPaddingX,
        boxSizing: 'border-box',
        [FOOTER_MOBILE_MQ]: { py: '32px', px: '20px' },
      }}
    >
      <Box
        sx={{
          maxWidth: 1520,
          mx: 'auto',
          width: '100%',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: footerSectionGap,
          [FOOTER_MOBILE_MQ]: { gap: '32px' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            justifyContent: { lg: 'space-between' },
            alignItems: { xs: 'flex-start', lg: 'flex-start' },
            gap: { xs: '32px', lg: '77px' },
            width: '100%',
            [FOOTER_MOBILE_MQ]: { gap: '32px' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: footerBrandGap,
              width: { lg: 400 },
              maxWidth: { xs: '100%', lg: 400 },
              flexShrink: 0,
              [FOOTER_MOBILE_MQ]: { gap: '32px', maxWidth: 362 },
            }}
          >
            <Box
              component="img"
              src={footerLogo}
              alt="Disha Foundation"
              sx={{
                width: { xs: 62.63, lg: 92.11 },
                height: { xs: 68, lg: 100 },
                display: 'block',
                [FOOTER_MOBILE_MQ]: { width: 62.63, height: 68 },
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: 15, lg: 18 },
                lineHeight: 'normal',
                fontWeight: 400,
                color: '#111',
                opacity: 0.8,
                maxWidth: { xs: 362, lg: 400 },
                [FOOTER_MOBILE_MQ]: { fontSize: 15 },
              }}
            >
              {BRAND_DESCRIPTION}
            </Typography>
          </Box>

          <Divider
            sx={{
              borderColor: 'rgba(0, 0, 0, 0.12)',
              width: '100%',
              display: { xs: 'block', md: 'none' },
            }}
          />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
                lg: 'auto auto auto auto',
              },
              gap: footerColumnGap,
              width: { xs: '100%', lg: 'auto' },
              flex: { lg: '1 1 auto' },
              justifyContent: { lg: 'flex-end' },
              minWidth: 0,
              [FOOTER_MOBILE_MQ]: {
                display: 'flex',
                flexDirection: 'column',
                gap: '32px',
              },
            }}
          >
            <FooterColumn title="Quick Links">
              <LinkList links={footerQuickLinks} />
            </FooterColumn>

            <FooterColumn title="Programs">
              <LinkList links={footerProgramLinks} />
            </FooterColumn>

            <FooterColumn title="Get Involved">
              <LinkList links={footerGetInvolvedLinks} />
            </FooterColumn>

            <FooterColumn
              title="Contact Us"
              sx={{
                gridColumn: { sm: '1 / -1', lg: 'auto' },
                [FOOTER_MOBILE_MQ]: { gridColumn: 'auto' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                <ContactRow icon={locationIcon}>{contactInfo.location}</ContactRow>
                <ContactRow icon={phoneIcon} href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}>
                  {contactInfo.phone}
                </ContactRow>
                <ContactRow icon={mailIcon} href={`mailto:${contactInfo.email}`}>
                  {contactInfo.email}
                </ContactRow>
                <ContactRow icon={globeIcon} href={`mailto:${contactInfo.emailAlt}`}>
                  {contactInfo.emailAlt}
                </ContactRow>
              </Box>
            </FooterColumn>
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.12)' }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'center', md: 'center' },
            gap: { xs: '32px', md: 2 },
            width: '100%',
            [FOOTER_MOBILE_MQ]: {
              flexDirection: 'column',
              alignItems: 'center',
              gap: '32px',
            },
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 15, lg: 18 },
              lineHeight: 'normal',
              fontWeight: 400,
              color: '#111',
              opacity: 0.8,
              textAlign: 'left',
              alignSelf: { xs: 'stretch', lg: 'auto' },
              order: { xs: 2, lg: 0 },
              flex: { lg: '1 1 auto' },
              [FOOTER_MOBILE_MQ]: {
                fontSize: 15,
                order: 2,
                alignSelf: 'stretch',
                maxWidth: 327,
              },
            }}
          >
            © 2025 Disha Foundation, Surat. All rights reserved.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
              justifyContent: { xs: 'center', lg: 'flex-end' },
              flexShrink: 0,
              order: { xs: 1, lg: 1 },
              [FOOTER_MOBILE_MQ]: { order: 1 },
            }}
          >
            {socialLinks.map((item) => (
              <SocialButton key={item.label} label={item.label} url={item.url} />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
